import { Role } from '@robu/shared';
import { lazy, type ReactNode } from 'react';
import { Route, Routes } from 'react-router-dom';

import { AppShell } from './components/layout/AppShell';
import { NotFoundPage } from './components/NotFoundPage';
import {
  RedirectIfAuthenticated,
  RequireAuth,
  RequirePlayer,
  RequireRole,
} from './features/auth/guards';
import { RequireRatingInMenu } from './features/rounds/RequireRatingInMenu';

// Player screens.
const LoginPage = lazy(() => import('./features/auth/LoginPage'));
const AcceptInvitationPage = lazy(() => import('./features/auth/AcceptInvitationPage'));
const ForgotPasswordPage = lazy(() => import('./features/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./features/auth/ResetPasswordPage'));
const DashboardPage = lazy(() => import('./features/dashboard/DashboardPage'));
const MatchListPage = lazy(() => import('./features/matches/MatchListPage'));
const MatchDetailPage = lazy(() => import('./features/matches/MatchDetailPage'));
const MyPredictionsPage = lazy(() => import('./features/predictions/MyPredictionsPage'));
const BalanceHistoryPage = lazy(() => import('./features/ledger/BalanceHistoryPage'));
const RoundListPage = lazy(() => import('./features/rounds/RoundListPage'));
const RoundSummaryPage = lazy(() => import('./features/rounds/RoundSummaryPage'));
const OverallRatingPage = lazy(() => import('./features/rounds/OverallRatingPage'));
const SettingsPage = lazy(() => import('./features/settings/SettingsPage'));

// Admin panel.
const AdminLayout = lazy(() => import('./features/admin/AdminLayout'));
const AdminDashboardPage = lazy(() => import('./features/admin/AdminDashboardPage'));
const AdminRoundsPage = lazy(() => import('./features/admin/AdminRoundsPage'));
const AdminMatchesPage = lazy(() => import('./features/admin/AdminMatchesPage'));
const AdminTeamsPage = lazy(() => import('./features/admin/AdminTeamsPage'));
const AdminPlayersPage = lazy(() => import('./features/admin/AdminPlayersPage'));
const AdminPlayerDetailPage = lazy(() => import('./features/admin/AdminPlayerDetailPage'));
const AdminFinancePage = lazy(() => import('./features/admin/AdminFinancePage'));
const AdminAccountPage = lazy(() => import('./features/admin/AdminAccountPage'));

// SuperAdministrator-only sections.
const AdministratorsPage = lazy(() => import('./features/admin/super/AdministratorsPage'));
const PlatformSettingsPage = lazy(() => import('./features/admin/super/PlatformSettingsPage'));
const AuditLogPage = lazy(() => import('./features/admin/super/AuditLogPage'));
const ReversalsPage = lazy(() => import('./features/admin/super/ReversalsPage'));
const PlatformLedgerPage = lazy(() => import('./features/admin/super/PlatformLedgerPage'));

const ADMIN_ROLES: readonly Role[] = [Role.ADMINISTRATOR, Role.SUPER_ADMINISTRATOR];
const SUPER_ROLES: readonly Role[] = [Role.SUPER_ADMINISTRATOR];

/** Wraps a SuperAdministrator-only element; the API enforces this too (TZ §36). */
function SuperOnly({ children }: { children: ReactNode }) {
  return <RequireRole roles={SUPER_ROLES}>{children}</RequireRole>;
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Public: closed registration means these are invitation-driven. */}
      <Route
        path="/login"
        element={
          <RedirectIfAuthenticated>
            <LoginPage />
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path="/accept-invitation"
        element={
          <RedirectIfAuthenticated>
            <AcceptInvitationPage />
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <RedirectIfAuthenticated>
            <ForgotPasswordPage />
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path="/reset-password"
        element={
          <RedirectIfAuthenticated>
            <ResetPasswordPage />
          </RedirectIfAuthenticated>
        }
      />

      {/* Player area. Staff accounts are redirected to the admin panel. */}
      <Route element={<RequireAuth />}>
        <Route
          element={
            <RequirePlayer>
              <AppShell />
            </RequirePlayer>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="matches" element={<MatchListPage />} />
          <Route path="matches/:matchId" element={<MatchDetailPage />} />
          <Route path="predictions" element={<MyPredictionsPage />} />
          <Route path="balance" element={<BalanceHistoryPage />} />
          <Route path="rounds" element={<RoundListPage />} />
          <Route
            path="rounds/rating"
            element={
              <RequireRatingInMenu>
                <OverallRatingPage />
              </RequireRatingInMenu>
            }
          />
          <Route path="rounds/:roundId" element={<RoundSummaryPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* Admin panel (TZ §40). */}
        <Route element={<RequireRole roles={ADMIN_ROLES} />}>
          <Route path="admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboardPage />} />
            <Route path="rounds" element={<AdminRoundsPage />} />
            <Route path="rounds/rating" element={<OverallRatingPage />} />
            <Route path="matches" element={<AdminMatchesPage />} />
            <Route path="matches/:matchId" element={<MatchDetailPage />} />
            <Route path="rounds/:roundId" element={<RoundSummaryPage />} />
            <Route path="account" element={<AdminAccountPage />} />
            <Route path="teams" element={<AdminTeamsPage />} />
            <Route path="players" element={<AdminPlayersPage />} />
            <Route path="players/:playerId" element={<AdminPlayerDetailPage />} />
            <Route path="finance" element={<AdminFinancePage />} />

            <Route
              path="administrators"
              element={
                <SuperOnly>
                  <AdministratorsPage />
                </SuperOnly>
              }
            />
            <Route
              path="settings"
              element={
                <SuperOnly>
                  <PlatformSettingsPage />
                </SuperOnly>
              }
            />
            <Route
              path="audit-log"
              element={
                <SuperOnly>
                  <AuditLogPage />
                </SuperOnly>
              }
            />
            <Route
              path="reversals"
              element={
                <SuperOnly>
                  <ReversalsPage />
                </SuperOnly>
              }
            />
            <Route
              path="platform-ledger"
              element={
                <SuperOnly>
                  <PlatformLedgerPage />
                </SuperOnly>
              }
            />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}
